import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { User, Bell, BellOff, LogOut, ShieldCheck, Home, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money } from "../lib/format";
import { enablePush, disablePush, pushSupported } from "../lib/push";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

export default function Profile() {
  const { user, household, role, logout } = useAuth();
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const me = dash?.me;

  useEffect(() => {
    if (pushSupported() && "serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => {
        reg?.pushManager?.getSubscription().then((s) => setPushOn(!!s));
      });
    }
  }, []);

  const togglePush = async (val) => {
    setBusy(true);
    try {
      if (val) {
        await enablePush();
        setPushOn(true);
        toast.success("Browser notifications enabled");
      } else {
        await disablePush();
        setPushOn(false);
        toast("Browser notifications disabled");
      }
    } catch (e) {
      toast.error(e.message || "Could not update notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
        <User className="h-6 w-6 text-primary" /> Profile
      </h1>

      <div className="rounded-3xl border border-border bg-card p-6 flex items-center gap-4">
        <Avatar className="h-16 w-16 border border-border">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-primary/10 text-primary text-xl font-bold">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-heading font-bold text-lg truncate">{user?.name}</div>
          <div className="text-sm text-muted-foreground truncate flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{user?.email}</div>
          <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {role === "admin" ? <><ShieldCheck className="h-3 w-3" /> Household Admin</> : "Roommate"}
          </div>
        </div>
      </div>

      {me && (
        <div className="grid grid-cols-3 gap-3">
          <StatMini label="Paid this month" value={money(me.paid)} />
          <StatMini label="Your share" value={money(me.share)} />
          <StatMini label={me.balance >= 0 ? "You're owed" : "You owe"} value={money(Math.abs(me.balance))} tone={me.balance >= 0 ? "pos" : "neg"} />
        </div>
      )}

      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-1"><Home className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Household</span></div>
        <div className="text-sm text-muted-foreground">{household?.name}</div>
      </div>

      <div className="rounded-3xl border border-border bg-card p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            {pushOn ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
          </div>
          <div>
            <div className="font-semibold text-sm">Browser push notifications</div>
            <div className="text-xs text-muted-foreground">Get alerts even when the app is closed</div>
          </div>
        </div>
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : (
          <Switch checked={pushOn} onCheckedChange={togglePush} data-testid="push-toggle" disabled={!pushSupported()} />
        )}
      </div>

      <Button onClick={logout} variant="outline" className="w-full h-11 rounded-xl text-rose-600 border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-950/30" data-testid="profile-logout-btn">
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
}

function StatMini({ label, value, tone }) {
  const color = tone === "pos" ? "text-emerald-600 dark:text-emerald-400" : tone === "neg" ? "text-rose-600 dark:text-rose-400" : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-3.5">
      <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
      <div className={`font-heading font-bold text-lg mt-1 ${color}`}>{value}</div>
    </div>
  );
}
